import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Alert, CircularProgress, Checkbox, FormControlLabel, Divider } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../components/AuthProvider';

const WORKERS_URL = 'https://share2dm-webhook.share2dm.workers.dev';

async function initShare2dmBrand(providerToken: string, sellerProfileId: string, email: string) {
  try {
    await fetch(`${WORKERS_URL}/auth/init-from-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        access_token: providerToken,
        clozet_seller_id: sellerProfileId,
        notification_email: email,
      }),
    });
  } catch {
    // 비즈니스 계정 없거나 토큰 만료 시 무시 — DM 캠페인 페이지에서 재시도 가능
  }
}

type Role = 'brand' | 'creator';
type Step = 'role' | 'form' | 'cafe24';

export default function SellerRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [step, setStep] = useState<Step>('role');
  const [registeredSellerProfileId, setRegisteredSellerProfileId] = useState<string | null>(null);
  const [mallId, setMallId] = useState('');
  const [cafe24Loading, setCafe24Loading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [brandForm, setBrandForm] = useState({
    store_name: '', business_number: '', owner_name: '', contact_phone: '', contact_email: '',
  });
  const [creatorForm, setCreatorForm] = useState({
    store_name: '', category: '', owner_name: '', contact_phone: '', contact_email: '',
  });

  // OAuth 메타데이터로 자동 기입
  useEffect(() => {
    if (!user) return;
    const fullName: string = user.user_metadata?.full_name || user.user_metadata?.name || '';
    const email: string = user.email || '';

    setBrandForm(p => ({
      ...p,
      owner_name: p.owner_name || fullName,
      contact_email: p.contact_email || email,
    }));
    setCreatorForm(p => ({
      ...p,
      store_name: p.store_name || fullName,
      owner_name: p.owner_name || fullName,
      contact_email: p.contact_email || email,
    }));
  }, [user]);

  const handleRegister = async () => {
    if (!user) return;
    setIsLoading(true);
    setMessage(null);

    try {
      if (!termsAgreed) {
        setMessage({ type: 'error', text: '이용약관 및 개인정보처리방침에 동의해주세요.' });
        setIsLoading(false);
        return;
      }

      if (selectedRole === 'brand') {
        const { store_name, business_number, owner_name, contact_phone, contact_email } = brandForm;
        if (!store_name || !business_number || !owner_name || !contact_phone || !contact_email) {
          setMessage({ type: 'error', text: '모든 항목을 입력해주세요.' });
          return;
        }
        const { data: brandProfile, error } = await supabase.from('seller_profile').insert({
          user_id: user.id,
          store_name,
          business_number,
          owner_name,
          contact_phone,
          contact_email,
          status: 'pending',
        }).select('id').single();
        if (error) throw error;

        // share2dm brand 초기화 (백그라운드, 실패해도 무시)
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.provider_token && brandProfile?.id) {
          initShare2dmBrand(session.provider_token, brandProfile.id, contact_email);
        }

        // Cafe24 연동 단계로 이동
        setRegisteredSellerProfileId(brandProfile.id);
        setStep('cafe24');

      } else {
        const { store_name, owner_name, contact_phone, contact_email } = creatorForm;
        if (!store_name || !owner_name || !contact_phone || !contact_email) {
          setMessage({ type: 'error', text: '모든 항목을 입력해주세요.' });
          return;
        }
        const { data: creatorProfile, error } = await supabase.from('seller_profile').insert({
          user_id: user.id,
          store_name,
          bio: creatorForm.category,
          owner_name,
          contact_phone,
          contact_email,
          business_number: `CREATOR-${user.id.slice(0, 8)}`,
          status: 'active',
        }).select('id').single();
        if (error) throw error;
        await supabase.from('profiles').update({ role: 'creator' }).eq('id', user.id);

        // share2dm brand 초기화 (백그라운드, 실패해도 무시)
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.provider_token && creatorProfile?.id) {
          initShare2dmBrand(session.provider_token, creatorProfile.id, contact_email);
        }

        navigate('/creator');
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || '오류가 발생했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCafe24Connect = async () => {
    if (!mallId.trim() || !registeredSellerProfileId) return;
    setCafe24Loading(true);
    try {
      // share2dm_brands.id 조회 (clozet_seller_id로)
      const { data: brand } = await supabase
        .from('share2dm_brands')
        .select('id')
        .eq('clozet_seller_id', registeredSellerProfileId)
        .maybeSingle();

      if (!brand) {
        setMessage({ type: 'error', text: 'share2dm 연동 정보를 찾을 수 없습니다. 잠시 후 다시 시도해주세요.' });
        setCafe24Loading(false);
        return;
      }

      const cafe24Url = `${WORKERS_URL}/auth/cafe24?brand_id=${brand.id}&mall_id=${encodeURIComponent(mallId.trim().toLowerCase())}`;
      window.location.href = cafe24Url;
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
      setCafe24Loading(false);
    }
  };

  return (
    <Box sx={{
      width: '100vw', height: '100vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      bgcolor: '#f8f8f8',
    }}>
      <Box sx={{ width: '100%', maxWidth: 480, px: 3 }}>

        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 5, position: 'relative', justifyContent: 'center' }}>
          {step === 'form' && (
            <Button
              onClick={() => { setSelectedRole(null); setStep('role'); setMessage(null); }}
              startIcon={<ArrowBackIcon />}
              sx={{ position: 'absolute', left: 0, color: '#555', fontWeight: 600 }}
            >
              뒤로
            </Button>
          )}
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5}>Clozet</Typography>
        </Box>

        {step === 'cafe24' ? (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LinkIcon sx={{ fontSize: 48, color: '#111', mb: 1 }} />
              <Typography variant="h6" fontWeight={700} mb={0.5}>Cafe24 쇼핑몰 연동</Typography>
              <Typography color="text.secondary" fontSize={13}>
                Cafe24 쇼핑몰을 연동하면 상품을 자동으로 가져올 수 있습니다.
              </Typography>
            </Box>

            {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

            <TextField
              fullWidth
              label="Cafe24 쇼핑몰 ID"
              placeholder="예: myshop (myshop.cafe24.com)"
              value={mallId}
              onChange={e => setMallId(e.target.value)}
              helperText="Cafe24 관리자 URL에서 확인 가능합니다."
              sx={{ mb: 2 }}
            />

            <Button
              fullWidth variant="contained"
              onClick={handleCafe24Connect}
              disabled={cafe24Loading || !mallId.trim()}
              sx={{ py: 1.5, fontWeight: 700, fontSize: 15, borderRadius: 2, bgcolor: '#111', '&:hover': { bgcolor: '#333' }, mb: 1.5 }}
            >
              {cafe24Loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Cafe24 연동하기'}
            </Button>

            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">또는</Typography>
            </Divider>

            <Button
              fullWidth variant="outlined"
              onClick={() => navigate('/seller-pending')}
              sx={{ py: 1.5, fontWeight: 600, fontSize: 14, borderRadius: 2, borderColor: '#ddd', color: '#555', mt: 1 }}
            >
              나중에 하기 (건너뛰기)
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
              Cafe24를 사용하지 않는 경우 건너뛰세요. 나중에 설정에서 연동할 수 있습니다.
            </Typography>
          </>
        ) : step === 'role' ? (
          <>
            <Typography variant="h6" fontWeight={700} textAlign="center" mb={1}>
              어떤 방식으로 사용하시나요?
            </Typography>
            <Typography color="text.secondary" textAlign="center" fontSize={14} mb={4}>
              선택에 따라 맞춤 기능을 제공합니다.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => { setSelectedRole('brand'); setStep('form'); }} sx={{
                flex: 1, p: 3.5, borderRadius: 3, border: '2px solid #e0e0e0',
                bgcolor: '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                '&:hover': { borderColor: '#111', transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
              }}>
                <StorefrontIcon sx={{ fontSize: 36, mb: 1.5, color: '#111' }} />
                <Typography fontWeight={700} fontSize={16}>브랜드</Typography>
                <Typography color="text.secondary" fontSize={12} mt={0.5}>자사몰 연동 · 상품 판매</Typography>
              </Box>
              <Box onClick={() => { setSelectedRole('creator'); setStep('form'); }} sx={{
                flex: 1, p: 3.5, borderRadius: 3, border: '2px solid #e0e0e0',
                bgcolor: '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                '&:hover': { borderColor: '#111', transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
              }}>
                <StarIcon sx={{ fontSize: 36, mb: 1.5, color: '#111' }} />
                <Typography fontWeight={700} fontSize={16}>크리에이터</Typography>
                <Typography color="text.secondary" fontSize={12} mt={0.5}>릴스 · DM 캠페인 · 수익 창출</Typography>
              </Box>
            </Box>
          </>
        ) : step === 'form' ? (
          <>
            <Typography variant="h6" fontWeight={700} mb={0.5}>
              {selectedRole === 'brand' ? '브랜드 정보 입력' : '크리에이터 정보 입력'}
            </Typography>
            <Typography color="text.secondary" fontSize={13} mb={3}>
              {selectedRole === 'brand'
                ? '승인 후 상품 등록 및 판매를 시작할 수 있습니다.'
                : '입력 후 바로 시작하실 수 있습니다.'}
            </Typography>

            {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedRole === 'brand' ? (
                <>
                  <TextField fullWidth label="스토어명" placeholder="브랜드 이름"
                    value={brandForm.store_name} onChange={e => setBrandForm(p => ({ ...p, store_name: e.target.value }))} />
                  <TextField fullWidth label="사업자번호" placeholder="000-00-00000"
                    value={brandForm.business_number} onChange={e => setBrandForm(p => ({ ...p, business_number: e.target.value }))} />
                  <TextField fullWidth label="대표자명" placeholder="홍길동"
                    value={brandForm.owner_name} onChange={e => setBrandForm(p => ({ ...p, owner_name: e.target.value }))} />
                  <TextField fullWidth label="연락처" placeholder="010-0000-0000"
                    value={brandForm.contact_phone} onChange={e => setBrandForm(p => ({ ...p, contact_phone: e.target.value }))} />
                  <TextField fullWidth label="이메일" type="email" placeholder="contact@example.com"
                    value={brandForm.contact_email} onChange={e => setBrandForm(p => ({ ...p, contact_email: e.target.value }))}
                    helperText="서비스 오류 발생 시 해당 주소로 알림이 발송됩니다." />
                </>
              ) : (
                <>
                  <TextField fullWidth label="활동명" placeholder="인스타그램 닉네임 또는 채널명"
                    value={creatorForm.store_name} onChange={e => setCreatorForm(p => ({ ...p, store_name: e.target.value }))} />
                  <TextField fullWidth label="성명" placeholder="홍길동"
                    value={creatorForm.owner_name} onChange={e => setCreatorForm(p => ({ ...p, owner_name: e.target.value }))} />
                  <TextField fullWidth label="주요 카테고리" placeholder="패션, 뷰티, 라이프스타일 등"
                    value={creatorForm.category} onChange={e => setCreatorForm(p => ({ ...p, category: e.target.value }))} />
                  <TextField fullWidth label="연락처" placeholder="010-0000-0000"
                    value={creatorForm.contact_phone} onChange={e => setCreatorForm(p => ({ ...p, contact_phone: e.target.value }))} />
                  <TextField fullWidth label="이메일" type="email" placeholder="contact@example.com"
                    value={creatorForm.contact_email} onChange={e => setCreatorForm(p => ({ ...p, contact_email: e.target.value }))}
                    helperText="서비스 오류 발생 시 해당 주소로 알림이 발송됩니다." />
                </>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAgreed}
                    onChange={e => setTermsAgreed(e.target.checked)}
                    size="small"
                    sx={{ color: '#111', '&.Mui-checked': { color: '#111' } }}
                  />
                }
                label={
                  <Typography fontSize={12} color="text.secondary">
                    <a href="/terms/service" target="_blank" rel="noreferrer" style={{ color: '#111', fontWeight: 600 }}>이용약관</a>
                    {' '}및{' '}
                    <a href="/terms/privacy" target="_blank" rel="noreferrer" style={{ color: '#111', fontWeight: 600 }}>개인정보처리방침</a>
                    에 동의합니다.{selectedRole === 'creator' && ' (Clozet 크리에이터 네트워크 포함 동의 포함)'}
                  </Typography>
                }
              />
            </Box>

            <Button fullWidth variant="contained" onClick={handleRegister} disabled={isLoading || !termsAgreed}
              sx={{ mt: 1.5, py: 1.5, fontWeight: 700, fontSize: 15, borderRadius: 2, bgcolor: '#111', '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#ccc' } }}>
              {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : '시작하기'}
            </Button>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
